import { getLoggerManager } from '@/shared/logging'
import type { BrowserVideoMp4ExportPhaseEvent } from './BrowserVideoMp4Export'

const log = getLoggerManager().getLogger('videoRender:audio')

export interface PreparedVideoAudio {
  context: AudioContext
  buffer: AudioBuffer
}

/** Fetches template audio and creates an exactly video-length buffer. */
export async function prepareAudioBuffer(
  audioUrl: string,
  durationSeconds: number,
  signal?: AbortSignal,
  diagnosticId = '',
  onPhase?: (event: BrowserVideoMp4ExportPhaseEvent) => void
): Promise<PreparedVideoAudio | null> {
  const url = audioUrl.trim()
  if (!url || durationSeconds <= 0) return null
  let context: AudioContext | null = null
  const startedAt = getLoggerManager().now()
  logAudioPhase('audio.prepare', 'start', diagnosticId, startedAt, '', onPhase)
  try {
    const AudioContextCtor = resolveAudioContextCtor()
    if (!AudioContextCtor) {
      throw new Error('AudioContext is unavailable; cannot export the template audio.')
    }
    const response = await fetch(url, signal ? { signal } : undefined)
    if (!response.ok) {
      throw new Error(`audio request failed with HTTP ${response.status}`)
    }
    const sourceBytes = await response.arrayBuffer()
    log.info(
      `[videoRender] audio downloaded (${sourceBytes.byteLength} bytes, ` +
        `${response.headers.get('content-type') ?? 'unknown content type'}, ` +
        `elapsedMs=${(getLoggerManager().now() - startedAt).toFixed(1)})`
    )
    context = new AudioContextCtor()
    const decoded = await context.decodeAudioData(sourceBytes.slice(0))
    log.info(
      `[videoRender] audio decoded (${decoded.numberOfChannels}ch/${decoded.sampleRate}Hz, ` +
        `${decoded.duration.toFixed(3)}s, ` +
        `elapsedMs=${(getLoggerManager().now() - startedAt).toFixed(1)})`
    )
    if (!decoded.numberOfChannels || !decoded.length || decoded.sampleRate <= 0) {
      throw new Error('Decoded template audio is empty or invalid.')
    }
    const targetLength = Math.max(1, Math.ceil(durationSeconds * decoded.sampleRate))
    const output = context.createBuffer(decoded.numberOfChannels, targetLength, decoded.sampleRate)
    for (let channel = 0; channel < decoded.numberOfChannels; channel += 1) {
      const source = decoded.getChannelData(channel)
      const target = output.getChannelData(channel)
      for (let index = 0; index < target.length; index += 1) {
        target[index] = source[index % source.length] ?? 0
      }
    }
    logAudioPhase(
      'audio.prepare',
      'complete',
      diagnosticId,
      startedAt,
      `durationSeconds=${durationSeconds.toFixed(3)}`,
      onPhase
    )
    return { context, buffer: output }
  } catch (error) {
    if (!signal?.aborted) {
      log.error(`[videoRender] audio preparation failed: ${getErrorMessage(error)}`)
    }
    logAudioPhase(
      'audio.prepare',
      'failed',
      diagnosticId,
      startedAt,
      `errorName=${error instanceof Error ? error.name : typeof error}`,
      onPhase
    )
    await closeAudioContext(context)
    throw error
  }
}

function logAudioPhase(
  phase: 'audio.prepare',
  status: 'start' | 'complete' | 'failed',
  diagnosticId: string,
  startedAt: number,
  details = '',
  reporter?: (event: BrowserVideoMp4ExportPhaseEvent) => void
): void {
  const trace = diagnosticId ? ` requestId=${diagnosticId}` : ''
  const elapsed = status === 'start' ? 0 : Math.max(0, getLoggerManager().now() - startedAt)
  const suffix = details ? ` ${details}` : ''
  log.info(
    `[videoRender] phase=${phase} status=${status}${trace} elapsedMs=${elapsed.toFixed(1)}${suffix}`
  )
  reporter?.({ phase: 'audio.prepare', status, elapsedMs: elapsed })
}

export async function closeAudioContext(context: AudioContext | null): Promise<void> {
  if (!context || context.state === 'closed') return
  try {
    await context.close()
  } catch {
    // Audio cleanup is best effort and must not mask video completion.
  }
}

function resolveAudioContextCtor(): typeof AudioContext | null {
  const scope = globalThis as typeof globalThis & {
    webkitAudioContext?: typeof AudioContext
  }
  return typeof scope.AudioContext === 'function'
    ? scope.AudioContext
    : (scope.webkitAudioContext ?? null)
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}
